import { useLayoutEffect, useRef, useState } from 'react';

// Extra width the toolbar must gain before the labels come back, so a window
// resized right at the edge does not flip between the two looks.
const HYSTERESIS = 40;

/** Does anything in the filters stick out of their box? */
function overflows(toolbar) {
  const left = toolbar.querySelector('.board-filter-left');
  const filters = toolbar.querySelector('.task-board-toolbar-filters');
  if (toolbar.scrollWidth > toolbar.clientWidth + 1) return true;
  if (filters && filters.scrollWidth > filters.clientWidth + 1) return true;
  // Other toolbars (the Boards overview) mark the boxes to watch.
  for (const el of toolbar.querySelectorAll('[data-compact-measure]')) {
    if (el.scrollWidth > el.clientWidth + 1) return true;
  }
  // The row never wraps (flex-wrap: nowrap), so too much shows as overflow.
  return !!left && left.scrollWidth > left.clientWidth + 1;
}

/**
 * A board toolbar stays ONE row (owner, 13.10.2026; the Boards overview
 * since 14.10.2026): when the filters
 * no longer fit, the toolbar turns compact — the pills keep their icon (and
 * count), the text goes (tooltips and aria-labels keep the words). The width
 * at which it had to turn compact is remembered, and the labels come back
 * once the toolbar is clearly wider than that again.
 *
 * Measured after every render (filters, counts and "Clear N" change the
 * width) and on every resize of the toolbar.
 */
export default function useToolbarCompact(ref) {
  const [compact, setCompact] = useState(false);
  const fullNeeds = useRef(0);
  const [, setTick] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const bump = () => setTick((t) => t + 1);
    const ro = new ResizeObserver(bump);
    ro.observe(el);
    // The filter bar is memoised and re-renders on its own (a chip, a count,
    // "Clear N"), which neither resizes the toolbar nor re-renders it.
    const mo = typeof MutationObserver === 'undefined' ? null : new MutationObserver(bump);
    mo?.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      ro.disconnect();
      mo?.disconnect();
    };
  }, [ref]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !el.clientWidth) return undefined;
    if (!compact) {
      if (!overflows(el)) return undefined;
      // One frame later: the label chips fold into their dropdown first
      // (useLabelChipOverflow, same commit) — compact only if that was not enough.
      const raf = requestAnimationFrame(() => {
        if (ref.current && overflows(ref.current)) {
          fullNeeds.current = ref.current.clientWidth + 1;
          setCompact(true);
        }
      });
      return () => cancelAnimationFrame(raf);
    }
    if (el.clientWidth > fullNeeds.current + HYSTERESIS) setCompact(false);
    return undefined;
  });

  return compact;
}
